import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AddFragranceModal from '../components/AddFragranceModal';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const Wishlist = ({ token }) => {
  const isMobile = useIsMobile();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [removing, setRemoving] = useState(null);
  const [addItem, setAddItem] = useState(null);
  const [libraryIds, setLibraryIds] = useState([]);

  const API = process.env.REACT_APP_API_URL;

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const fetchAll = async () => {
    try {
      const [wishlistRes, libraryRes] = await Promise.allSettled([
        axios.get(`${API}/api/wishlist`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/library`,  { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (wishlistRes.status === 'fulfilled') setWishlist(wishlistRes.value.data);
      if (libraryRes.status  === 'fulfilled') setLibraryIds(libraryRes.value.data.map(i => i.fragrance_id));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleRemove = async (id) => {
    setRemoving(id);
    try {
      await axios.delete(`${API}/api/wishlist/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(prev => prev.filter(i => i.id !== id));
    } catch (err) { console.error(err); }
    setRemoving(null);
  };

  const isInLibrary = (fragrance_id) => libraryIds.includes(fragrance_id);

  const filtered = wishlist.filter(item =>
    !search ||
    item.name.toLowerCase().includes(search.toLowerCase()) ||
    item.brand.toLowerCase().includes(search.toLowerCase())
  );

  const s = {
    container: { minHeight: '100vh', padding: isMobile ? '100px 16px 48px' : '120px 48px 48px', maxWidth: '1200px', margin: '0 auto' },
    header: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: isMobile ? '20px' : '0', marginBottom: '32px', borderBottom: '1px solid #2a2a2a', paddingBottom: '32px' },
    eyebrow: { fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '12px' },
    title: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2.2rem' : '3rem', fontWeight: 300, color: '#f5f0eb' },
    empty: { textAlign: 'center', padding: '80px 0' },
    emptyTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px', fontStyle: 'italic' },
    emptyText: { fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: '#a09890', marginBottom: '40px' },
    grid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1px', background: '#2a2a2a', border: '1px solid #2a2a2a' },
    card: { background: '#111111', padding: isMobile ? '24px 20px' : '32px', display: 'flex', flexDirection: 'column' },
  };

  if (loading) return (
    <div style={{ ...s.container, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: '#c9a96e', fontStyle: 'italic' }}>Loading your wishlist...</p>
    </div>
  );

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <p style={s.eyebrow}>Saved for Later</p>
          <h1 style={s.title}>My Wishlist</h1>
        </div>
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', color: '#3a3a3a', letterSpacing: '0.1em' }}>
          {wishlist.length} fragrance{wishlist.length !== 1 ? 's' : ''}
        </p>
      </div>

      {wishlist.length === 0 ? (
        <div style={s.empty}>
          <p style={s.emptyTitle}>Your wishlist is empty</p>
          <p style={s.emptyText}>Save fragrances you want to try or buy from the Discover page</p>
          <a href="/discover" style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#0a0a0a', background: '#c9a96e', padding: '14px 32px', textDecoration: 'none', display: 'inline-block' }}>Browse Fragrances</a>
        </div>
      ) : (
        <>
          {/* Search */}
          <div style={{ marginBottom: '32px' }}>
            <input
              type="text"
              placeholder="Search your wishlist..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', background: '#111111', border: '1px solid #2a2a2a', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.9rem', fontWeight: 300, padding: '14px 24px', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }}
            />
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: search ? '#c9a96e' : '#3a3a3a' }}>
              {search ? `${filtered.length} of ${wishlist.length} fragrances` : `${wishlist.length} fragrance${wishlist.length !== 1 ? 's' : ''}`}
            </p>
          </div>

          {filtered.length === 0 ? (
            <div style={s.empty}>
              <p style={s.emptyTitle}>No matches found</p>
              <button onClick={() => setSearch('')} style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a09890', background: 'none', border: '1px solid #2a2a2a', padding: '14px 32px', cursor: 'pointer' }}>Clear Search</button>
            </div>
          ) : (
            <div style={s.grid}>
              {filtered.map(item => {
                const inLibrary = isInLibrary(item.fragrance_id);
                return (
                  <div key={item.id} style={s.card}>
                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', fontWeight: 400, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' }}>{item.brand}</p>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '8px' }}>{item.name}</p>
                    {item.type && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#a09890', marginBottom: '8px', letterSpacing: '0.1em' }}>{item.type}</p>}
                    {item.main_accord && <span style={{ display: 'inline-block', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a09890', border: '1px solid #2a2a2a', padding: '4px 10px', marginBottom: '16px' }}>{item.main_accord}</span>}

                    {/* Price */}
                    <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 300, color: item.price_gbp ? '#c9a96e' : '#3a3a3a', marginBottom: '20px' }}>
                      {item.price_gbp ? `£${item.price_gbp}` : '—'}
                    </span>

                    {/* Actions */}
                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '20px', borderTop: '1px solid #1a1a1a' }}>
                      {inLibrary ? (
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#665e58', border: '1px solid #2a2a2a', padding: '10px 16px', textAlign: 'center' }}>
                          Already in Library
                        </span>
                      ) : (
                        <button
                          onClick={() => setAddItem(item)}
                          onMouseEnter={e => { e.currentTarget.style.background = '#c9a96e'; e.currentTarget.style.color = '#0a0a0a'; e.currentTarget.style.borderColor = '#c9a96e'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#c9a96e'; e.currentTarget.style.borderColor = '#c9a96e'; }}
                          style={{ background: 'none', border: '1px solid #c9a96e', color: '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '10px 16px', cursor: 'pointer', transition: 'all 0.2s ease', width: '100%' }}
                        >
                          + Add to Library
                        </button>
                      )}
                      <button
                        onClick={() => handleRemove(item.id)}
                        disabled={removing === item.id}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#665e58'; e.currentTarget.style.color = '#a09890'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#6b6460'; }}
                        style={{ background: 'none', border: '1px solid #2a2a2a', color: '#6b6460', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '10px 16px', cursor: 'pointer', opacity: removing === item.id ? 0.5 : 1, transition: 'all 0.2s ease', width: '100%' }}
                      >
                        {removing === item.id ? 'Removing...' : 'Remove from Wishlist'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {addItem && (
        <AddFragranceModal
          token={token}
          onClose={() => setAddItem(null)}
          onSuccess={() => {
            setAddItem(null);
            // Mark as in library and remove from wishlist
            setLibraryIds(prev => [...prev, addItem.fragrance_id]);
            setWishlist(prev => prev.filter(i => i.fragrance_id !== addItem.fragrance_id));
          }}
          preselected={addItem}
        />
      )}
    </div>
  );
};

export default Wishlist;